import { Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { decimalSeparator, thousandSeparator } from "../../../i18n";
import KiloNewton from "../../Math/Units/Metric/KiloNewton";
import KiloNewtonMillimetre from "../../Math/Units/Metric/KiloNewtonMillimetre";
import Metre from "../../Math/Units/Metric/Metre";
import Millimetre from "../../Math/Units/Metric/Millimetre";
import { DataRow, DataRowTextValue } from "../DataRow";
import { PageWrapper, styles, tableStyles } from "../SmartTitanProjektPDF";

export function GermanyInnereDruckKnickenOfner({ data }) {
  const { t } = useTranslation("translation");
  let itemIndex = 1;
  return (
    <>
      <PageWrapper Deckblatt={data.Deckblatt}>
        <View>
          {/* Pfahlkopf / Übergangsrohr */}
          <Text style={styles.section}>{t("inner.buckling.buttonOfner")}</Text>

          {/* Parameter Stahltragglied */}
          <Text style={styles.sectionSubText}>
            {t("inner.buckling.ofner.parameterTragglied.headline")}
          </Text>

          {/* (Daten aus Zulassung, bzw. aus Zulassung umgerechnet) */}
          <Text style={styles.sectionSubText3}>
            {t("inner.buckling.ofner.parameterTragglied.headlineSub")}
          </Text>

          <View style={tableStyles.tableContainer}>
            {/* Wahl des Stahltraggliedes */}
            <DataRowTextValue
              item={{
                title: t("inner.verificationCapacity.selectedLoadCarryingElement"),
                value: data.Germany.Innere.NachweisStahltragglied.wahlDesStahltraggliedes,

                itemIndex: itemIndex++,
              }}
            />
            {/* Bemessungslast */}
            <DataRow
              item={{
                title: t("inner.fdCalculation.designLoad"),
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}F_{d}",
                  height: 7,
                  width: 9,
                },
                value: data.Germany.Innere.BeanspruchungausStatik.bemessungslast,
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: <KiloNewton />,
              }}
            />
            {/* Querschnitt */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.crossSection"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.Querschnitt,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}A_{s}",
                  height: 7,
                  width: 9,
                },
                decimalSeparator: decimalSeparator,
                thousandSeparator: thousandSeparator,
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: "mm²",
              }}
            />
            {/* Innendurchmesser */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.innerDiameter"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.Innendurchmesser,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}D_{i}",
                  height: 7,
                  width: 9,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 1,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
              }}
            />
            {/* Äußerer Durchmesser */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.outerDiameter"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.aeußererDurchmesser,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}D_{a,cal}",
                  height: 9,
                  width: 21,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 1,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
                // "https://latex.codecogs.com/png.image?\dpi{110}&space;\bg_white&space;\inline&space;D_{a,cal}=\sqrt{{\frac{4\cdot&space;A_s}{\pi}}&plus;{D_i^2}" title="\bg_white \inline D_{a,cal}=\sqrt{{\frac{4\cdot A_s}{\pi}}+{D_i^2}"
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;D_{a,cal}=\\sqrt{{\\frac{4\\cdot&space;A_s}{\\pi}}&plus;{D_i}^2}",
                  height: 15,
                  width: 78,
                },
              }}
            />
            {/* Dehnsteifigkeit */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.axialStiffness"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.Dehnsteifigkeit,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}EA_{s}",
                  height: 7,
                  width: 17,
                },
                decimalSeparator: decimalSeparator,
                thousandSeparator: thousandSeparator,
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <KiloNewton />,
              }}
            />
            {/* Elastizitätsmodul */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.modulusOfElasticity"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.Elastizitaetsmodul,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}E",
                  height: 7,
                  width: 7,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: "kN/mm²",
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;E=\\frac{EA_s}{A_s}",
                  height: 11,
                  width: 31,
                },
              }}
            />
            {/* Trägheitsmoment */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.momentOfInertia"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.Traegheitsmoment,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}I",
                  height: 6,
                  width: 4,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}mm^4
                unitImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\textbf{mm}^4",
                  height: 7,
                  width: 19,
                },

                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;I=\\frac{\\pi}{64}\\cdot(D_{a,cal}^4-D_i^4)",
                  height: 11,
                  width: 75,
                },
              }}
            />
            {/* Biegesteifigkeit */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.flexuralStiffness"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.Biegesteifigkeit,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}EI",
                  height: 6,
                  width: 11,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: "kNmm²",
              }}
            />
            {/* Wiederstandsmoment */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.sectionModulus"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.Wiederstandsmoment,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}W_{s}",
                  height: 7,
                  width: 11,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: "mm³", //<MillimetreCubed />,
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;W_s=\\frac{\\pi}{32}\\cdot\\frac{D_{a,cal}^4-D_i^4}{D_{a,cal}}",
                  height: 16,
                  width: 70,
                },
              }}
            />
            {/* Imperfektionsbeiwert */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.imperfectionFactor"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.Imperfektionsbeiwert,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}\\small{\\alpha}",
                  height: 4,
                  width: 5,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
            {/* charakteristische Festigkeit */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.characteristicStrengh"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied
                    .charakteristischeFestigkeit,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}f_{y,k}",
                  height: 9,
                  width: 13,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 3,
                itemIndex: itemIndex++,
                valueUnit: "kN/mm²", //<KiloNewtonDividedByMillimetreSquared />,
              }}
            />
            {/* Teilsicherheitsbeiwert */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterTragglied.partialSafetyFactor"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterStahltragglied.Teilsicherheitsbeiwert,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}\\gamma_{_{M}}",
                  height: 6,
                  width: 11,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
          </View>

          {/* Parameter Verpresskörper */}
          <Text style={styles.sectionSubText}>
            {t("inner.buckling.ofner.parameterGroutingBody.headline")}
          </Text>
          <View style={tableStyles.tableContainer}>
            {/* Verpresskörperdurchmesser */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterGroutingBody.groutBody"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterVerpresskoerper
                    .Verpresskoerperdurchmesser,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\small&space;\\dpi{110}\\bg{white}D_{c}",
                  height: 7,
                  width: 10,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;D_c=D&plus;a_{min}",
                  height: 7,
                  width: 54,
                },
              }}
            />
            {/* Querschnitt Verpresskörper */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterGroutingBody.cementCrossSection"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterVerpresskoerper
                    .QuerschnittVerpresskoerper,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;A_c",
                  height: 7,
                  width: 9,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: "mm²",
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;A_c=\\frac{\\pi}{4}\\cdot&space;\\(D_c^2-D_{a,cal}^2\\)",
                  height: 11,
                  width: 80,
                },
              }}
            />
            {/* Stärke des Zylinders */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterGroutingBody.cylinderStrength"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterVerpresskoerper.StaerkeDesZylinders,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;f_{c,k}",
                  height: 9,
                  width: 14,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 3,
                itemIndex: itemIndex++,
                valueUnit: "kN/mm²", //<KiloNewtonDividedByMillimetreSquared />,
              }}
            />
            {/* Teilsicherheitsbeiwert Zement */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterGroutingBody.partialSafetyFactorCement"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterVerpresskoerper
                    .TeilsicherheitsbeiwertZement,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\gamma_c",
                  height: 6,
                  width: 7,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
          </View>

          {/* Tragfähigkeit Mikropfahl */}
          <Text style={styles.sectionSubText}>
            {t("inner.buckling.ofner.loadbearingCapacitiesMicropile.headline")}
          </Text>
          <View style={tableStyles.tableContainer}>
            {/* Bemessungswert der Normalkraft-Tragfähigkeit */}
            <DataRow
              item={{
                title: t(
                  "inner.buckling.ofner.loadbearingCapacitiesMicropile.designValueNormalForceBearingCapacity"
                ),
                value:
                  data.Germany.Innere.Knicken.Ofner.TragfaehigkeitenMikropfahl
                    .BemessungswertDerNormalkraftTragfaehigkeit,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;N_{Rd}",
                  height: 7,
                  width: 15,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <KiloNewton />,
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;N_{Rd}=A_s\\cdot&space;\\frac{f_{yk}}{\\gamma_{\\tiny_M}}&plus;0,85\\cdot&space;A_c\\cdot&space;0,85\\cdot&space;\\frac{f_{ck}}{\\gamma_c}",
                  height: 13,
                  width: 138,
                },
              }}
            />

            {/* Bemessungswert der Momenten-Tragfähigkeit */}
            <DataRow
              item={{
                title: t(
                  "inner.buckling.ofner.loadbearingCapacitiesMicropile.designValueMomentanForceBearingCapacity"
                ),
                value:
                  data.Germany.Innere.Knicken.Ofner.TragfaehigkeitenMikropfahl
                    .BemessungswertDerMomentenTragfaehigkeit,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;M_{Rd}",
                  height: 7,
                  width: 16,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <KiloNewtonMillimetre />,
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;M_{Rd}=W_s\\cdot&space;\\frac{f_{yk}}{\\gamma_{\\tiny_M}}",
                  height: 13,
                  width: 56,
                },
              }}
            />
          </View>
        </View>
      </PageWrapper>
      <PageWrapper Deckblatt={data.Deckblatt}>
        <View>
          {/* Parameter Baugrund */}
          <Text style={styles.sectionSubText}>
            {t("inner.buckling.ofner.parameterSoil.headline")}
          </Text>
          <View style={tableStyles.tableContainer}>
            {/* Dicke weiche Bodenschicht */}
            <DataRow
              item={{
                title: t("inner.buckling.lengthWeakSoilLayer"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterBaugrund.DickeWeicheBodenschicht,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;L",
                  height: 6,
                  width: 5,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: <Metre />,
              }}
            />
            {/* undränierte Scherfestigkeit */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterSoil.undrainedCohesion"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterBaugrund.undraenierteScherfestigkeit,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;c_{u,k}",
                  height: 7,
                  width: 12,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: "kN/m²",
              }}
            />
            {/* Teilsicherheitsbeiwert für geotechnischen Kenngrößen */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterSoil.partialSafeFactor"),
                value:
                  data.Germany.Innere.Knicken.Ofner.ParameterBaugrund
                    .TeilsicherheitsbeiwertFuerGeotechnischenKenngroeßen,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\gamma_{cu}",
                  height: 6,
                  width: 11,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
            {/* Koeffizient nach Wenz */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterSoil.bucklingCoefficientWenz"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterBaugrund.KoeffizientNachWenz,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;k_c",
                  height: 7,
                  width: 7,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
            {/* Koeffizient nach Gudehus */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.parameterSoil.bucklingCoefficientGudenhaus"),
                value: data.Germany.Innere.Knicken.Ofner.ParameterBaugrund.KoeffizientNachGudehus,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;k_y",
                  height: 9,
                  width: 8,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 1,
                itemIndex: itemIndex++,
              }}
            />
          </View>

          {/* Nachweise */}
          <Text style={styles.sectionSubText}>
            {t("inner.buckling.ofner.Verification.headline")}
          </Text>
          <View style={tableStyles.tableContainer}>
            {/* Fließdruck der seitlichen Bettung */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.flowPressureLateralBedding"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.FließdruckDerSeitlichenBettung,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;q_y",
                  height: 7,
                  width: 7,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 4,
                itemIndex: itemIndex++,
                valueUnit: "kN/mm",
                formulaImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;q_y=k_c\\cdot&space;D_c\\cdot&space;\\frac{c_{u,k}}{\\gamma_{cu}}",
                  height: 12,
                  width: 60,
                },
              }}
            />
            {/* elastischer Bereich der seitlichen Bettung */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.elasticSoilReaction"),
                value:
                  data.Germany.Innere.Knicken.Ofner.Nachweise
                    .elastischerBereichDerSeitlichenBettung,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;w_y",
                  height: 7,
                  width: 9,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;w_y=k_y\\cdot&space;D_c
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;w_y=k_y\\cdot&space;D_c",
                  height: 9,
                  width: 45,
                },
              }}
            />
            {/* Bettungsziffer */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.beddingFactor"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.Bettungsziffer,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;c",
                  height: 4,
                  width: 4,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 4,
                itemIndex: itemIndex++,
                valueUnit: "kN/mm²", //<NewtonDividedByMillimetreSquared />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;c=&space;\\frac{q_y}{w_y}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;c=&space;\\frac{q_y}{w_y}",
                  height: 11,
                  width: 24,
                },
              }}
            />
            {/* Anzahl der Halbwellen */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.numberHalfCycles"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.AnzahlDerHalbwellen,
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;m",
                  height: 4,
                  width: 7,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;m=\\frac{L}{\\pi}\\cdot&space;\\sqrt[4]{\\frac{c}{EI_s}}"
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;m=\\frac{L}{\\pi}\\cdot&space;\\sqrt[4]{\\frac{c}{EI_s}}",
                  height: 15,
                  width: 52,
                },
              }}
            />
            {/* Halbwellenlänge */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.lengthHalfcycles"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.Halbwellenlaenge,
                bezImage: {
                  //"https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;L_{Hw}"
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;L_{Hw}",
                  height: 7,
                  width: 16,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;L_{Hw}=\\frac{L}{m}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;L_{Hw}=\\frac{L}{m}",
                  height: 11,
                  width: 34,
                },
              }}
            />
            {/* Schlankheit des Ersatzstabes */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.slendernessEquivalentBar"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.SchlankheitDesErsatzstabes,
                bezImage: {
                  //"https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\overline{\\lambda}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\overline{\\lambda}",
                  height: 7,
                  width: 5,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
                //valueUnit: <Metre />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\overline{\\lambda}=\\sqrt{\\frac{A_s\\cdot&space;f_{yk}}{\\overline{N}_{ki}}}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\overline{\\lambda}=\\sqrt{\\frac{A_s\\cdot&space;f_{yk}}{\\overline{N}_{ki}}}",
                  height: 15,
                  width: 46,
                },
              }}
            />
            {/* Knicklast des Ersatzstabes */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.bucklingLoadEquivalentBar"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.KnicklastDesErsatzstabes,
                bezImage: {
                  //\overline N_{k,i}
                  // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\overline&space;N_{k,i}
                  // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\overline&space;N_{k,i}
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\overline&space;N_{k,i}",
                  height: 10,
                  width: 15,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <KiloNewton />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\overline{N}_{ki}=\\frac{\\pi^2\\cdot&space;EI_s}{{L_{H_w}}^2}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\overline{N}_{ki}=\\frac{\\pi^2\\cdot&space;EI_s}{{L_{H_w}}^2}",
                  height: 14,
                  width: 46,
                },
              }}
            />
            {/* Knicklast eines gebetteten Pfahls */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.bucklingLoadEmbeddedPile"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.KnicklastEinesGebettetenPfahls,
                bezImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;N_{ki}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;N_{ki}",
                  height: 7,
                  width: 12,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <KiloNewton />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;N_{ki}=\\overline{N}_{ki}&plus;\\frac{c\\cdot&space;{L_{Hw}}^2}{\\pi^2}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;N_{ki}=\\overline{N}_{ki}&plus;\\frac{c\\cdot&space;{L_{Hw}}^2}{\\pi^2}",
                  height: 12,
                  width: 71,
                },
              }}
            />
            {/* Imperfektionsamplitude */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.equivalentImperfectionAmplitude"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.Imperfektionsamplitude,
                bezImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;e_0
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;e_{&space;_0}",
                  height: 6,
                  width: 7,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;e_0=\\alpha\\cdot&space;(\\overline{\\lambda}-0,2)\\cdot&space;\\frac{W_s}{A_s}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;e_{&space;_0}=\\alpha\\cdot&space;(\\overline{\\lambda}-0,2)\\cdot&space;\\frac{W_s}{A_s}",
                  height: 11,
                  width: 80,
                },
              }}
            />
            {/* Biegemoment des Pfahls */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.bendingmoment"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.BiegemomentDesPfahls,
                bezImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;M_{Fd}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;M_{Fd}",
                  height: 7,
                  width: 16,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 0,
                itemIndex: itemIndex++,
                valueUnit: <KiloNewtonMillimetre />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;M_{Fd}=N_{Fd}\\cdot&space;e_0\\cdot&space;\\[\\frac{\\overline{N}_{ki}}{N_{ki}-N_{Fd}}\\]
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;M_{Fd}=N_{Fd}\\cdot&space;e_{&space;_0}\\cdot&space;\\[\\frac{\\overline{N}_{ki}}{N_{ki}-N_{Fd}}\\]",
                  height: 12,
                  width: 97,
                },
              }}
            />
            {/* Verformungsamplitude */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.lateralDeformationAmplitude"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.Verformungsamplitude,
                bezImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;w_0
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;w_{&space;_0}",
                  height: 6,
                  width: 9,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
                valueUnit: <Millimetre />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;w_0=e_0\\cdot&space;\\frac{N_{Fd}}{N_{ki}-N_{Fd}}
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;w_{&space;_0}=e_{&space;_0}\\cdot&space;\\frac{N_{Fd}}{N_{ki}-N_{Fd}}",
                  height: 12,
                  width: 63,
                },
              }}
            />
            {/* Querschnittsnachweis */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.crossSectionVerification"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.Querschnittsnachweis,
                bezImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;w_0
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\eta",
                  height: 6,
                  width: 4,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
                //valueUnit: <Millimetre />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\eta=\frac{N_{Fd}}{N_{Rd}}&plus;\\frac{M_{Fd}}{M_{Rd}}\\leq&space;1,0
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\eta=\\frac{N_{Fd}}{N_{Rd}}&plus;\\frac{M_{Fd}}{M_{Rd}}\\leq&space;1,0",
                  height: 12,
                  width: 79,
                },
              }}
            />
            {/* Verformungsbegrenzung */}
            <DataRow
              item={{
                title: t("inner.buckling.ofner.Verification.deflectionVerifications"),
                value: data.Germany.Innere.Knicken.Ofner.Nachweise.Verformungsbegrenzung,
                bezImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\eta
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\eta",
                  height: 6,
                  width: 4,
                },
                decimalSeparator: decimalSeparator(),
                thousandSeparator: thousandSeparator(),
                valueScale: 2,
                itemIndex: itemIndex++,
                //valueUnit: <Millimetre />,
                formulaImage: {
                  //https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\eta=\\frac{w_0}{w_y}\\leq&space;1,0
                  uri: "https://latex.codecogs.com/png.image?\\dpi{110}&space;\\bg_white&space;\\inline&space;\\eta=\\frac{w_{&space;_0}}{w_y}\\leq&space;1,0",
                  height: 11,
                  width: 48,
                },
              }}
            />
            {/* <InvoiceTableBlankSpace rowsCount={25 - itemIndex} /> */}
          </View>
        </View>
      </PageWrapper>
    </>
  );
}
